/**
 * Serviço para gerenciar perfis de acesso
 */

import { getToken } from "./tokenService";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface Permission {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_active: boolean;
  parent_id?: number | null;
  level?: number;
  permission_type?: string;
  display_order?: number;
  icon?: string;
  is_category?: boolean;
}

export interface Profile {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  users_count: number;
  permissions: Permission[];
  created_at: string;
  updated_at: string;
}

export interface ProfilesListResponse {
  data: Profile[];
  total: number;
  limit: number;
  offset: number;
}

export interface ProfilesStats {
  total: number;
  active: number;
  inactive: number;
  top_profiles: {
    id: string;
    name: string;
    user_count: string;
  }[];
}

export interface CreateProfileRequest {
  name: string;
  description?: string;
  is_active?: boolean;
  permission_ids?: number[];
}

export interface UpdateProfileRequest {
  name?: string;
  description?: string;
  is_active?: boolean;
  permission_ids?: number[];
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista perfis com filtros e paginação
 */
export async function listProfiles(options?: {
  limit?: number;
  offset?: number;
  is_active?: boolean;
  search?: string;
}): Promise<ApiResponse<ProfilesListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.is_active !== undefined)
    params.append("is_active", options.is_active.toString());
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  return apiRequest<ProfilesListResponse>(
    `/profiles${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Obtém um perfil por ID
 */
export async function getProfileById(id: string): Promise<ApiResponse<Profile>> {
  return apiRequest<Profile>(`/profiles/${id}`);
}

/**
 * Cria um novo perfil
 */
export async function createProfile(
  profileData: CreateProfileRequest
): Promise<ApiResponse<Profile>> {
  return apiRequest<Profile>("/profiles", "POST", profileData);
}

/**
 * Atualiza um perfil
 */
export async function updateProfile(
  id: string,
  profileData: UpdateProfileRequest
): Promise<ApiResponse<Profile>> {
  return apiRequest<Profile>(`/profiles/${id}`, "PUT", profileData);
}

/**
 * Remove um perfil
 */
export async function deleteProfile(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/profiles/${id}`, "DELETE");
}

/**
 * Altera o status de um perfil
 */
export async function changeProfileStatus(
  id: string,
  isActive: boolean
): Promise<ApiResponse<Profile>> {
  return apiRequest<Profile>(`/profiles/${id}/status`, "PATCH", {
    is_active: isActive,
  });
}

/**
 * Lista todas as permissões disponíveis
 * @param options - Opções de filtro
 * @param options.asTree - Se true, retorna estrutura hierárquica. Se false, retorna lista plana (padrão: false)
 */
export async function listPermissions(options?: {
  asTree?: boolean;
}): Promise<ApiResponse<Permission[]>> {
  const queryParams = new URLSearchParams();
  if (options?.asTree !== undefined) {
    queryParams.append("tree", options.asTree.toString());
  } else {
    // Por padrão, retornar lista plana para o PermissionTree
    queryParams.append("tree", "false");
  }

  const queryString = queryParams.toString();
  return apiRequest<Permission[]>(`/profiles/permissions${queryString ? `?${queryString}` : ""}`);
}

/**
 * Obtém estatísticas dos perfis
 */
export async function getProfilesStats(): Promise<ApiResponse<ProfilesStats>> {
  return apiRequest<ProfilesStats>("/profiles/stats");
}

